import {Box, Paper} from "@mui/material";
import ErrorSnackBar from "../../CommonComponents/ErrorSnackBar/ErrorSnackBar";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import React, {useEffect, useRef, useState} from "react";
import {useTheme} from "../../../contexts/theme";
import {RowJobTable} from "../../../types/adminPageTypes";
import {FormJob} from "../../../types/jobTypes";
import {getJobById, stopJob} from "../../../queries/jobQueries";
import {getDefaultDataJob} from "../../../utils/jobUtils";
import JobForm from "../../MatchboardContent/JobForm/JobForm";
import StyledPopup from "../../CommonComponents/StyledPopup/StyledPopup";
import {formatUTCDate} from "../../../utils/dateTimeutils";
import {restartJobFromTracks} from "../../../queries/adminPageQueries";
import {rowsJobsTablePageSize} from "../../../utils/constants";
import CustomPagination from "../../CommonComponents/CustomPagination/CustomPagination";
import ReplayIcon from '@mui/icons-material/Replay';
import StyledIconButton from "../../CommonComponents/StyledIconButton/StyledIconButton";
import StopIcon from '@mui/icons-material/Stop';

interface JobsTableProps {
    rows: RowJobTable[],
    columns: GridColDef[],
    loading: boolean,
    page: number;
    handlePage: (page: number) => void;
    onJobStop: (jobId: string) => void;
}

const defaultJobData = getDefaultDataJob()
const iconButtonDimension = '35px'

export default function RestartJobTable({columns, rows, loading, handlePage, page, onJobStop}: JobsTableProps) {

    const theme = useTheme();
    const [tableCols, setTableCols] = useState(columns);
    const [jobData, setJobData] = useState<FormJob>(JSON.parse(JSON.stringify(defaultJobData)));
    const [selectedJob, setSelectedJob] = useState<RowJobTable | undefined>(undefined)
    const [jobFormOpen, setJobFormOpen] = useState(false);
    const [jobLoading, setJobLoading] = useState(false);
    const [stoppingSet, setStoppingSet] = useState<Set<string>>(new Set())
    const stoppingSetRef = useRef<Set<string>>(new Set())

    const [errorMessage, setErrorMessage] = useState<{error: boolean, message: string}>({
        error:false,
        message: "",
    })

    const iconStyle = {
        backgroundColor: 'transparent',
        fill: theme.palette.white,
        fontSize: '20px',
    }

    async function onRestartClick(row: RowJobTable){
        try{
            setJobFormOpen(true);
            setJobLoading(true);
            setSelectedJob(row);
            const job = await getJobById(row.id);
            setJobData(job);
            setJobLoading(false);
        }catch (e) {
            onJobFormClosure()
            setErrorMessage({error: true, message:"Error retrieving job" + e});
        }
    }

    async function onStopClick(row: RowJobTable){
        try{
            const jobId = row.id
            let tempStoppingSet = new Set<string>(stoppingSetRef.current)
            tempStoppingSet.add(jobId)
            setStoppingSet(tempStoppingSet)
            await stopJob(row.id)
            onJobStop(jobId)
            tempStoppingSet = new Set<string>(tempStoppingSet)
            tempStoppingSet.delete(jobId)
            setStoppingSet(tempStoppingSet)
            setErrorMessage({error: true, message:"Job stopped successfully" });
        }catch (e) {
            setErrorMessage({error: true, message:"Error stopping job" + e});
        }
    }

    function onJobFormClosure(){
        setJobData(JSON.parse(JSON.stringify(defaultJobData)))
        setSelectedJob(undefined)
        setJobFormOpen(false);
    }

    async function jobFormSubmit(){
        try{
            await restartJobFromTracks(jobData);
            setErrorMessage({ error: true, message: 'Job restarted successfully' });
        }catch (e) {
            setErrorMessage({ error: true, message: 'Error restarting the job' });
        }finally {
            onJobFormClosure()
        }
    }

    useEffect(() => {
        const tempCols = [...columns];
        tempCols.push({
            field: "actions",
            headerName: "",
            width: 125,
            sortable: false,
            renderCell: (params) => (
                <Box display={'flex'} justifyContent={'space-around'} alignItems={'center'} height={'100%'}>
                    {params.row.status !== 'Running' && <StyledIconButton width={iconButtonDimension} height={iconButtonDimension} onClick= {() => onRestartClick(params.row)}>
                        <ReplayIcon sx={iconStyle}/>
                    </StyledIconButton>}
                    {params.row.status === 'Running' &&
                        <StyledIconButton disabled={stoppingSetRef.current.has(params.row.id)} width={iconButtonDimension} height={iconButtonDimension} onClick= {() => onStopClick(params.row)}>
                            <StopIcon sx={iconStyle}/>
                        </StyledIconButton>
                    }
                </Box>
            ),
        })
        setTableCols(tempCols);
    }, [columns, stoppingSet])

    useEffect(() => {
        stoppingSetRef.current = stoppingSet
    }, [stoppingSet]);

    return (
        <Paper
            sx={{
                height: 'auto',
                marginRight: `${theme.spacing.md}`,
                overflowY: 'auto',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '& .super-app-theme--header': {
                    backgroundColor: 'transparent',
                },
                paddingTop: theme.spacing.md
            }}
        >
            <ErrorSnackBar
                autoHideDuration={2000}
                open={errorMessage.error}
                setClose={()=>{setErrorMessage({error: false, message: ""})}}
                errorMessage={errorMessage.message}
            />
            <DataGrid
                rows={rows}
                columns={tableCols}
                hideFooter={true}
                columnVisibilityModel={{
                    id: false,
                    event_id: false,
                    competition_id: false,
                    competition_season: false,
                    competition_type: false
                }}
                loading={loading}
                sx={{
                    border: `2px solid ${theme.palette.white}`,
                    color: theme.palette.white,
                    backgroundColor: theme.palette.blue,
                    '& .MuiDataGrid-main': { backgroundColor: theme.palette.blue },
                    '& .MuiDataGrid-columnHeaders': { backgroundColor: theme.palette.blue },
                    '& .MuiDataGrid-columnHeader': { backgroundColor: theme.palette.blue },
                    '& .MuiDataGrid-row': { backgroundColor: theme.palette.blue },
                    '& .MuiDataGrid-footerContainer': { backgroundColor: theme.palette.blue },
                    '&.MuiDataGrid-root': {
                        '--DataGrid-containerBackground': theme.palette.blue,
                    },
                    '& .MuiTablePagination-root': {
                        color: theme.palette.white, // Override text color
                    },
                    '& .MuiTablePagination-selectIcon': {
                        color: theme.palette.white, // Change the color of the dropdown icon
                    },
                }}
            />
            <StyledPopup
                isOpen={jobFormOpen}
                onClose={onJobFormClosure}
                title={`Restart Job ${selectedJob ? selectedJob.event_name : ''} ${formatUTCDate(selectedJob ? selectedJob.event_date : '')}`}
                fullWidth={false}
                width={'70%'}
            >
                {selectedJob && <JobForm
                    eventId={selectedJob.event_id}
                    job={jobData}
                    onSubmit={jobFormSubmit}
                    setJob={setJobData}
                    mode={'update'}
                    competition={{
                        id: selectedJob.competition_id ,
                        name: selectedJob.competition_name ,
                        season: selectedJob.competition_season,
                        type: selectedJob.competition_type
                    }}
                    jobLoading={jobLoading}
                    submitLabel={'Restart Job'}
                />}
            </StyledPopup>
            <CustomPagination page={page} onPageChange={handlePage} disableArrowRight={rows.length < rowsJobsTablePageSize}/>
        </Paper>

    )
}